import { useKhalAuth } from '@khal-os/sdk/app';
import { useNotificationStore } from '@khal-os/ui';
import { Command, Lock, RefreshCw, Search } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasDesktopEntryPermission, useLaunchApp } from '../hooks/useLaunchApp';
import { useDesktopStore } from '../stores/desktop-store';
import type { DesktopEntry } from '../types/desktop-entry';
import { AppIcon } from './app-icon';

const RECENT_APPS_KEY = 'khal-os:recent-apps';
const MAX_RECENT = 5;

function getRecentAppIds(): string[] {
	if (typeof window === 'undefined') return [];
	try {
		const raw = localStorage.getItem(RECENT_APPS_KEY);
		if (raw) return JSON.parse(raw);
	} catch {
		/* ignore */
	}
	return [];
}

function saveRecentAppId(id: string) {
	try {
		const ids = getRecentAppIds().filter((x) => x !== id);
		ids.unshift(id);
		localStorage.setItem(RECENT_APPS_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)));
	} catch {
		/* ignore */
	}
}

interface CommandPaletteProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	serverPermissions?: string[];
}

type AppRow = {
	kind: 'app';
	id: string;
	title: string;
	desc: string;
	entry: DesktopEntry;
};

type ActionRow = {
	kind: 'action';
	id: string;
	title: string;
	desc: string;
	run: () => void;
};

type Row = AppRow | ActionRow;

export function CommandPalette({ open, onOpenChange, serverPermissions }: CommandPaletteProps) {
	const apps = useDesktopStore((s) => s.apps);
	const launchApp = useLaunchApp();
	const auth = useKhalAuth();
	const permissions = auth?.permissions ?? serverPermissions ?? [];
	const toggleNotifCenter = useNotificationStore((s) => s.toggleCenter);
	const [search, setSearch] = useState('');
	const [cursor, setCursor] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);

	const close = useCallback(() => onOpenChange(false), [onOpenChange]);

	const availableApps = useMemo(
		() =>
			apps
				.filter((entry) => hasDesktopEntryPermission(entry, permissions))
				.sort((a, b) => a.name.localeCompare(b.name)),
		[apps, permissions]
	);

	const launchAndClose = useCallback(
		(entry: DesktopEntry) => {
			saveRecentAppId(entry.id);
			launchApp(entry);
			close();
		},
		[launchApp, close]
	);

	const actions = useMemo<ActionRow[]>(
		() => [
			{
				kind: 'action',
				id: 'toggle-notifications',
				title: 'Toggle notifications',
				desc: 'Open / close the notification center',
				run: () => {
					toggleNotifCenter();
					close();
				},
			},
			{
				kind: 'action',
				id: 'reload',
				title: 'Reload desktop',
				desc: 'Refresh the shell',
				run: () => {
					if (typeof window !== 'undefined') window.location.reload();
				},
			},
			{
				kind: 'action',
				id: 'lock-screen',
				title: 'Lock screen',
				desc: '⌃⌘L',
				run: () => {
					console.warn('[command-palette] TODO: lock screen');
					close();
				},
			},
		],
		[toggleNotifCenter, close]
	);

	const filteredApps = useMemo<AppRow[]>(() => {
		const q = search.trim().toLowerCase();
		return availableApps
			.filter((a) => {
				if (!q) return true;
				return (
					a.name.toLowerCase().includes(q) ||
					a.id.toLowerCase().includes(q) ||
					(a.comment ?? '').toLowerCase().includes(q)
				);
			})
			.map((entry) => ({
				kind: 'app' as const,
				id: entry.id,
				title: entry.name,
				desc: entry.comment ?? `khal://${entry.id}`,
				entry,
			}));
	}, [availableApps, search]);

	const filteredActions = useMemo<ActionRow[]>(() => {
		const q = search.trim().toLowerCase();
		if (!q) return actions;
		return actions.filter((a) => a.title.toLowerCase().includes(q) || a.desc.toLowerCase().includes(q));
	}, [actions, search]);

	const flat = useMemo<Row[]>(() => [...filteredApps, ...filteredActions], [filteredApps, filteredActions]);

	useEffect(() => {
		setCursor(0);
	}, [search]);

	useEffect(() => {
		if (!open) {
			setSearch('');
			setCursor(0);
		}
	}, [open]);

	useEffect(() => {
		if (open) requestAnimationFrame(() => inputRef.current?.focus());
	}, [open]);

	const activate = useCallback(
		(row: Row) => {
			if (row.kind === 'app') {
				launchAndClose(row.entry);
			} else {
				row.run();
			}
		},
		[launchAndClose]
	);

	useEffect(() => {
		if (!open) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				e.stopPropagation();
				close();
				return;
			}
			if (e.key === 'ArrowDown') {
				e.preventDefault();
				setCursor((c) => Math.min(c + 1, Math.max(flat.length - 1, 0)));
				return;
			}
			if (e.key === 'ArrowUp') {
				e.preventDefault();
				setCursor((c) => Math.max(c - 1, 0));
				return;
			}
			if (e.key === 'Enter') {
				e.preventDefault();
				const row = flat[cursor];
				if (row) activate(row);
			}
		};
		window.addEventListener('keydown', handleKeyDown, { capture: true });
		return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
	}, [open, close, flat, cursor, activate]);

	if (!open) return null;

	let runningIdx = -1;

	return (
		<div className="khal-palette-veil khal-anim-fade-in fixed inset-0 z-[9998] flex flex-col items-center pt-[10vh]">
			<button
				type="button"
				aria-label="Dismiss command palette"
				className="absolute inset-0"
				style={{
					background: 'rgba(0, 0, 0, 0.55)',
					backdropFilter: 'blur(24px) saturate(1.3)',
					WebkitBackdropFilter: 'blur(24px) saturate(1.3)',
				}}
				onClick={close}
			/>

			<div
				role="dialog"
				aria-label="Command palette"
				className="khal-anim-scale-in k-popover relative z-10 w-full max-w-[520px] flex flex-col gap-2"
				style={{ maxWidth: 520, padding: 0 }}
			>
				<div className="k-input k-input-search" style={{ margin: 8, marginBottom: 0 }}>
					<Search size={14} />
					<input
						ref={inputRef}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search apps, run commands…"
						aria-label="Search apps and actions"
					/>
					<span className="k-kbd">esc</span>
				</div>

				<div
					className="k-menu"
					role="listbox"
					style={{
						margin: 8,
						marginTop: 4,
						maxHeight: '60vh',
						overflowY: 'auto',
						boxShadow: 'none',
						border: 0,
						padding: 4,
					}}
				>
					{flat.length === 0 ? (
						<div className="k-empty" style={{ padding: '32px 16px' }}>
							<div className="k-empty-icon">?</div>
							<p>No matches</p>
						</div>
					) : (
						<>
							{filteredApps.length > 0 && (
								<>
									<div className="k-menu-h">Apps</div>
									{filteredApps.map((row) => {
										runningIdx++;
										const active = runningIdx === cursor;
										const idx = runningIdx;
										const appId = row.entry.component || row.entry.exec || row.entry.id;
										return (
											<button
												key={row.id}
												type="button"
												role="option"
												aria-selected={active}
												className={`k-menu-item${active ? ' k-active' : ''}`}
												onMouseEnter={() => setCursor(idx)}
												onClick={() => activate(row)}
												style={{
													width: '100%',
													justifyContent: 'flex-start',
													gap: 10,
													padding: '8px 10px',
													border: 0,
													background: active ? undefined : 'transparent',
													textAlign: 'left',
												}}
											>
												<span style={{ width: 22, display: 'inline-flex', justifyContent: 'center' }}>
													<AppIcon appId={appId} size={18} />
												</span>
												<span style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
													<div className="k-list-item-title" style={{ lineHeight: 1.2 }}>
														{row.title}
													</div>
													<div
														className="k-list-item-desc"
														style={{
															lineHeight: 1.2,
															marginTop: 1,
															whiteSpace: 'nowrap',
															overflow: 'hidden',
															textOverflow: 'ellipsis',
														}}
													>
														{row.desc}
													</div>
												</span>
											</button>
										);
									})}
								</>
							)}

							{filteredActions.length > 0 && (
								<>
									<div className="k-menu-h">Actions</div>
									{filteredActions.map((row) => {
										runningIdx++;
										const active = runningIdx === cursor;
										const idx = runningIdx;
										return (
											<button
												key={row.id}
												type="button"
												role="option"
												aria-selected={active}
												className={`k-menu-item${active ? ' k-active' : ''}`}
												onMouseEnter={() => setCursor(idx)}
												onClick={() => activate(row)}
												style={{
													width: '100%',
													justifyContent: 'flex-start',
													gap: 10,
													padding: '8px 10px',
													border: 0,
													background: active ? undefined : 'transparent',
													textAlign: 'left',
												}}
											>
												<span style={{ width: 22, display: 'inline-flex', justifyContent: 'center' }}>
													{row.id === 'lock-screen' ? (
														<Lock size={14} />
													) : row.id === 'reload' ? (
														<RefreshCw size={14} />
													) : (
														<Command size={14} />
													)}
												</span>
												<span style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
													<div className="k-list-item-title" style={{ lineHeight: 1.2 }}>
														{row.title}
													</div>
												</span>
												<span className="k-kbd">{row.desc}</span>
											</button>
										);
									})}
								</>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
}
